/**
 * Notificaciones del navegador y sonido. El permiso solo se pide con un clic de la persona
 * (Ajustes → Notificaciones), nunca solo al cargar la página.
 */
export type BrowserPermission = 'granted' | 'denied' | 'default' | 'unsupported';

export function browserPermission(): BrowserPermission {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
  return Notification.permission;
}

export async function requestBrowserPermission(): Promise<BrowserPermission> {
  if (browserPermission() === 'unsupported') return 'unsupported';
  return Notification.requestPermission();
}

/**
 * Muestra la notificación del sistema solo si la pestaña no está a la vista (si lo está, basta
 * el aviso del panel). Al hacer clic enfoca la pestaña y abre el enlace.
 */
export function showBrowserNotification(
  title: string,
  body: string,
  options: { tag?: string; onClick?: () => void } = {},
): void {
  if (browserPermission() !== 'granted') return;
  if (document.visibilityState === 'visible' && document.hasFocus()) return;
  try {
    const notification = new Notification(title, {
      body,
      tag: options.tag,
      icon: '/favicon-96x96.png',
    });
    notification.onclick = () => {
      window.focus();
      options.onClick?.();
      notification.close();
    };
  } catch {
    /* algunos navegadores solo permiten notificaciones desde un service worker */
  }
}

let audio: AudioContext | null = null;

/** Dos tonos cortos y suaves (sin archivos de audio). */
export function playChime(): void {
  try {
    const Context =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Context) return;
    audio ??= new Context();
    const start = audio.currentTime;
    [880, 1318.5].forEach((frequency, index) => {
      const oscillator = audio!.createOscillator();
      const gain = audio!.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.value = frequency;
      const at = start + index * 0.12;
      gain.gain.setValueAtTime(0.0001, at);
      gain.gain.exponentialRampToValueAtTime(0.08, at + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.25);
      oscillator.connect(gain).connect(audio!.destination);
      oscillator.start(at);
      oscillator.stop(at + 0.3);
    });
  } catch {
    /* sin audio disponible: se ignora */
  }
}
